import React from "react";
import { useIntl } from "react-intl";
import { Box, Button, Grid, Link, Typography } from "@material-ui/core";
import { getLocalizedValue } from "../../core/lang";
import { MeetingHeader } from "./MeetingHeader";
import { MeetingProductAccordion } from "./MeetingProductAccordion";
import CustomIcon from "../../core/custom-icon";
import { Link as RouterLink } from "react-router-dom";
import { MeetingProps } from "./index";
import { ViewAs } from "../../corporation/hooks/meeting";

type Props = {
  comment?: string;
  documents?: string[];
} & MeetingProps;

export const MeetingEndedPanel = ({
  expert,
  corporation,
  locale,
  date,
  productName,
  productDescription,
  viewAs,
  participant,
  comment,
  documents,
}: Props) => {
  const { formatMessage, formatDate } = useIntl();
  return (
    <Box>
      <MeetingHeader
        expert={viewAs === ViewAs.Expert ? participant : expert}
        corporation={corporation}
        locale={locale}
        date={date}
        title={formatMessage({
          id: "MeetingSummaryTitle",
          defaultMessage: "Vielen Dank für Ihre Teilnahme.",
        })}
        text={formatMessage(
          {
            id: "MeetingSummaryText",
            defaultMessage: `Im Rahmen der WindEnergy Hamburg vom 1.12. bis 4.12.2020 haben Sie 
                            am {dayName}, den {startDate} einen virtuellen Gesprächstermin mit {expertName} von {corpName}
                            wahrgenommen. Hier finden Sie die Zusammenfassung des Termins sowie bereitgestellte 
                            Informationen zum Download.`,
          },
          {
            dayName: formatDate(date.startDate, {
              weekday: "long",
            }),
            startDate: formatDate(date.startDate),
            corpName: getLocalizedValue(locale, corporation.corpName),
            expertName: expert.fullName,
          }
        )}
      />

      <Grid container spacing={5}>
        <Grid item md={6}>
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              color="primary"
              gutterBottom
            >
              {formatMessage({
                id: "MeetingEndedDocumentUpload",
                defaultMessage: "Verfügbare Dokumente",
              })}
            </Typography>
            <Box mt={3.5}>
              {documents?.map((d) => (
                <Box mb={2}>
                  <Link href={d} underline="none">
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      color="primary.main"
                    >
                      <Box>
                        <Typography component="h4" variant="h4" gutterBottom>
                          {d.substring(d.lastIndexOf("/") + 1)}
                        </Typography>
                        {/* <Typography variant="body2">Produktkatalog</Typography> */}
                      </Box>
                      <CustomIcon iconname="download" />
                    </Box>
                  </Link>
                </Box>
              ))}
            </Box>
          </Box>
        </Grid>
        <Grid item md={6}>
          <Box pl={{ xs: 0, md: 7 }} pr={{ xs: 0, md: 7 }}>
            <Box mb={{ xs: 2, md: 3 }}>
              <Box mb={{ xs: 3, md: 3 }}>
                <Typography component="h3" variant="subtitle1" color="primary">
                  {formatMessage({
                    id: "MeetingLobbyComment",
                    defaultMessage: "Anliegen präzisieren",
                  })}
                </Typography>
              </Box>
              <MeetingProductAccordion
                locale={locale}
                productName={productName}
                productDescription={productDescription}
              />
            </Box>

            {comment && (
              <Typography variant="body1" color="primary">
                {comment}
              </Typography>
            )}

            <Box
              display="flex"
              flexDirection={{ xs: "column-reverse", md: "row" }}
              justifyContent="space-between"
              pt={{ xs: 5, md: 7.5 }}
            >
              <Box
                display={{ xs: "block", md: "inline-block" }}
                mt={{ xs: 1.5, md: 0 }}
              >
                <Button
                  variant="text"
                  startIcon={<CustomIcon iconname="calendar" />}
                  fullWidth
                >
                  {formatMessage({
                    id: "MeetingSummaryNewMeetingButton",
                    defaultMessage: "Neuen Termin",
                  })}
                </Button>
              </Box>
              <Button component={RouterLink} to="/">
                {formatMessage({
                  id: "MeetingSummaryDashboardButton",
                  defaultMessage: "Zum Dashboard",
                })}
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
